import { useCallback, useEffect, useState } from 'react';

import {
  PeerParticipant,
  PeerTeam,
  useBulkResendParticipantRegistrationEmail,
  useShowCampaignSettings,
} from '@givebacks/api/store';
import { CellContext, useManagementTranslations } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { Divider, notifications } from 'ui';
import { useClipboard } from 'ui/Hooks';
import {
  IconAdd,
  IconCircleCheck,
  IconClose,
  IconCopy,
  IconDelete,
  IconEditTabler,
  IconMail,
} from 'ui/Icons';

import { useCampaignShortLinks } from '../../../../hooks/useCampaignShortLinks';
import { AddEditPeerModal } from '../../../Settings/Peers/AddEditPeerModal';
import { RemovePeerModal } from '../../../Settings/Peers/RemovePeerModal';
import { SendRegistrationEmailModal } from '../../../Settings/Peers/SendRegistrationEmailModal';
import { ActivityLogModal } from '../../ActivityLogModal/ActivityLogModal';
import { FulfillPrizesModal } from '../prizeFulfillment/FulfillPrizesModal';
import { UnfulfillPrizesModal } from '../prizeFulfillment/UnfulfillPrizesModal';
import { usePeerPrizeReport } from './hooks';
import { PrizesReportTableInstance } from './instance';

const TeamRowActions = ({ row }: CellContext<PeerTeam, unknown>) => {
  const { table } = PrizesReportTableInstance.useContext();
  const { causeId, campaignId } = table.options.meta;
  const { t } = useManagementTranslations({ storeId: causeId, storeItemId: campaignId });
  const { data: campaignSettings } = useShowCampaignSettings({ campaignId, causeId });

  const [isOpened, setIsOpened] = useState<'edit' | 'delete' | 'fulfill' | 'unfulfill'>();

  const {
    hasEarnedPrizes,
    hasFulfilledPrizes,
    earnedPrizes,
    fulfilledPrizes,
    allEarnedPrizesFulfilled,
  } = usePeerPrizeReport(row.original.prize_reports);

  if (campaignSettings.isCampaignFinished) return null;

  return (
    <>
      <PrizesReportTableInstance.Table.RowAction>
        <PrizesReportTableInstance.Table.RowAction.Option
          label={`Edit ${t('team').toLowerCase()} info`}
          icon={IconEditTabler}
          onClick={() => setIsOpened('edit')}
        />
        {hasEarnedPrizes && (
          <PrizesReportTableInstance.Table.RowAction.Option
            label="Mark Prizes Fulfilled"
            icon={IconCircleCheck}
            onClick={() => setIsOpened('fulfill')}
            visible={!allEarnedPrizesFulfilled}
          />
        )}
        {hasFulfilledPrizes && (
          <PrizesReportTableInstance.Table.RowAction.Option
            label="Mark Prizes Unfulfilled"
            icon={IconClose}
            onClick={() => setIsOpened('unfulfill')}
          />
        )}
        <Divider mx="sm" />
        <PrizesReportTableInstance.Table.RowAction.Option
          label={`Remove ${t('team').toLowerCase()}`}
          icon={IconDelete}
          destructive
          onClick={() => setIsOpened('delete')}
        />
      </PrizesReportTableInstance.Table.RowAction>

      <AddEditPeerModal
        uuid={row.original.uuid}
        opened={isOpened === 'edit'}
        onClose={() => setIsOpened(undefined)}
      />
      <RemovePeerModal
        uuid={row.original.uuid}
        name={row.original.name}
        opened={isOpened === 'delete'}
        onClose={() => setIsOpened(undefined)}
      />
      <FulfillPrizesModal
        opened={isOpened === 'fulfill'}
        onClose={() => setIsOpened(undefined)}
        peerType="teams"
        peerIds={[row.original.uuid]}
        peerName={row.original.name}
        earnedPrizes={earnedPrizes}
      />
      <UnfulfillPrizesModal
        opened={isOpened === 'unfulfill'}
        onClose={() => setIsOpened(undefined)}
        peerType="teams"
        peerId={row.original.uuid}
        peerName={row.original.name}
        fulfilledPrizes={fulfilledPrizes}
      />
    </>
  );
};

const ParticipantRowActions = ({ row }: CellContext<PeerParticipant, unknown>) => {
  const { table } = PrizesReportTableInstance.useContext();
  const { causeId, campaignId } = table.options.meta;
  const { t } = useManagementTranslations({ storeId: causeId, storeItemId: campaignId });
  const { data: campaignSettings } = useShowCampaignSettings({ campaignId, causeId });
  const { participantCenterUrl } = useCampaignShortLinks();
  const { mutate: resendParticipantRegistrationEmail } =
    useBulkResendParticipantRegistrationEmail();
  const [isOpened, setIsOpened] = useState<
    'edit' | 'delete' | 'activity' | 'registration' | 'fulfill' | 'unfulfill'
  >();
  const { copied, copy, error } = useClipboard({ timeout: 500 });

  const {
    hasEarnedPrizes,
    hasFulfilledPrizes,
    earnedPrizes,
    fulfilledPrizes,
    allEarnedPrizesFulfilled,
  } = usePeerPrizeReport(row.original.prize_reports);

  useEffect(() => {
    if (copied) {
      notifications.show({
        variant: 'success',
        message: `${t('participant')} page link copied to clipboard`,
      });
    }
  }, [copied, t]);

  useEffect(() => {
    if (error) {
      notifications.show({
        variant: 'error',
        message: `Could not copy ${t('participant')} page link to clipboard`,
      });
    }
  }, [error, t]);

  const handleCopyParticipantPageLink = useCallback(() => {
    copy(`${participantCenterUrl}/participants/${row.original.uuid}`);
  }, [copy, participantCenterUrl, row.original.uuid]);

  const handleSendRegistrationEmail = () => {
    if (!causeId || !campaignId) return;
    if (!row.original.email) {
      setIsOpened('registration');
    } else {
      resendParticipantRegistrationEmail(
        { campaignId, causeId, uuids: [row.original.uuid] },
        {
          onSuccess() {
            notifications.show({
              variant: 'success',
              title: 'Registration email sent',
              message: `Sent to ${row.original.email} `,
            });
          },
          onError(error) {
            notifications.show({ message: getErrorMessage_v2(error), variant: 'error' });
          },
        }
      );
    }
  };

  if (campaignSettings.isCampaignFinished) return null;

  return (
    <>
      <PrizesReportTableInstance.Table.RowAction>
        <PrizesReportTableInstance.Table.RowAction.Option
          label={`Edit ${t('participant').toLowerCase()} info`}
          icon={IconEditTabler}
          onClick={() => setIsOpened('edit')}
        />
        <PrizesReportTableInstance.Table.RowAction.Option
          label="Log/edit activity"
          icon={IconAdd}
          onClick={() => setIsOpened('activity')}
          visible={campaignSettings.type === 'athon'}
        />

        <PrizesReportTableInstance.Table.RowAction.Option
          label={`Copy ${t('participant').toLowerCase()} page link`}
          icon={IconCopy}
          onClick={handleCopyParticipantPageLink}
        />

        <PrizesReportTableInstance.Table.RowAction.Option
          label="Send registration email"
          icon={IconMail}
          onClick={handleSendRegistrationEmail}
        />

        <PrizesReportTableInstance.Table.RowAction.Option
          label="Mark Prizes Fulfilled"
          icon={IconCircleCheck}
          onClick={() => setIsOpened('fulfill')}
          visible={hasEarnedPrizes && !allEarnedPrizesFulfilled}
        />

        <PrizesReportTableInstance.Table.RowAction.Option
          label="Mark Prizes Unfulfilled"
          // TODO: import circle close icon @acdodd17
          icon={IconClose}
          onClick={() => setIsOpened('unfulfill')}
          visible={hasFulfilledPrizes}
        />

        <Divider mx="sm" />
        <PrizesReportTableInstance.Table.RowAction.Option
          label={`Remove ${t('participant').toLowerCase()}`}
          icon={IconDelete}
          destructive
          onClick={() => setIsOpened('delete')}
        />
      </PrizesReportTableInstance.Table.RowAction>

      <AddEditPeerModal
        uuid={row.original.uuid}
        opened={isOpened === 'edit'}
        onClose={() => setIsOpened(undefined)}
      />
      <ActivityLogModal
        participant={row.original}
        open={isOpened === 'activity'}
        onClose={() => setIsOpened(undefined)}
      />
      <SendRegistrationEmailModal
        participantData={row.original}
        opened={isOpened === 'registration'}
        onClose={() => setIsOpened(undefined)}
      />
      <RemovePeerModal
        uuid={row.original.uuid}
        name={`${row.original.first_name} ${row.original.last_name}`}
        opened={isOpened === 'delete'}
        onClose={() => setIsOpened(undefined)}
      />
      <FulfillPrizesModal
        opened={isOpened === 'fulfill'}
        onClose={() => setIsOpened(undefined)}
        peerType="participants"
        peerIds={[row.original.uuid]}
        peerName={`${row.original.first_name} ${row.original.last_name}`}
        earnedPrizes={earnedPrizes}
      />
      <UnfulfillPrizesModal
        opened={isOpened === 'unfulfill'}
        onClose={() => setIsOpened(undefined)}
        peerType="participants"
        peerId={row.original.uuid}
        peerName={`${row.original.first_name} ${row.original.last_name}`}
        fulfilledPrizes={fulfilledPrizes}
      />
    </>
  );
};

export { ParticipantRowActions, TeamRowActions };
